import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { SelectGroupsDialogData } from '@app/components/dialogs/select-groups-dialog/select-groups-dialog-data';
import { ProjectParticipantItemComponent } from '@app/components/project-participants/project-participant-item/project-participant-item.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { GroupsDataService } from '@app/shared/services/groups-data.service';
import { ProjectParticipantsDataService } from '@app/shared/services/project-participants-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { SelectMode } from '@app/shared/types/enums/select-mode.enum';
import { Company } from '@app/shared/types/models/company/company.model';
import { Group } from '@app/shared/types/models/group/group.model';
import { ProjectParticipant } from '@app/shared/types/models/project/project-participant';

@Component({
  selector: 'proffeo-project-participants-for-company',
  imports: [MatIconModule, ProjectParticipantItemComponent, NgClass, UiButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './project-participants-for-company.component.html'
})
export class ProjectParticipantsForCompanyComponent {
  private readonly groupsDataService: GroupsDataService = inject(GroupsDataService);
  private readonly dialogService: DialogService = inject(DialogService);

  private readonly loadCompanyGroupsOnCompanyChange = effect(() => {
    const id = this.company().id;
    if (id) {
      this.groupsDataService.loadCompanyGroups(id);
    }
  });
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly projectsDataService: ProjectsDataService = inject(ProjectsDataService);
  private readonly projectParticipantsDataService: ProjectParticipantsDataService =
    inject(ProjectParticipantsDataService);

  protected companyGroups: Signal<Group[]> = computed(() => {
    if (!this.company()) {
      return [];
    }
    return this.groupsDataService.getCompanyGroups(this.company().id)() ?? [];
  });

  protected alreadySelectedGroups: Signal<Group[]> = computed(() =>
    this.participants()?.length ? [...new Set(this.participants().map(_ => _.group()))] : []
  );

  protected childrenVisible: WritableSignal<boolean> = signal(true);

  public company = input.required<Company>();
  public participants = input.required<ProjectParticipant[]>();

  protected selectGroups(): void {
    if (!this.company() || !this.participants() || !this.activeProjectService.activeProjectId()) {
      return;
    }

    const data: SelectGroupsDialogData = {
      groups: this.companyGroups,
      selectedGroups: this.alreadySelectedGroups() ?? [],
      lockedGroups: [],
      selectionMode: SelectMode.MULTIPLE
    };
    this.dialogService
      .openSelectGroupsDialog(data)
      .afterClosed()
      .subscribe((selectedGroups: Group[] | undefined) => {
        if (!selectedGroups) return;
        const selectedIds = selectedGroups.map(g => g.id);
        const toAdd = selectedIds.filter(id => !this.alreadySelectedGroups().some(g => g.id === id));
        if (toAdd.length) {
          this.projectParticipantsDataService.addGroups(this.activeProjectService.activeProjectId(), toAdd);
        }
        const toRemove = this.alreadySelectedGroups().filter(group => !selectedIds.includes(group.id));
        if (toRemove.length) {
          toRemove.forEach(group =>
            this.projectParticipantsDataService.removeGroupById(this.activeProjectService.activeProjectId(), group.id)
          );
        }
      });
  }

  protected participantsForGroup(group: Group): ProjectParticipant[] {
    if (!this.participants()?.length) {
      return [];
    }

    return this.participants().filter(_ => _.group().id === group.id);
  }

  protected toggleChildren(): void {
    this.childrenVisible.set(!this.childrenVisible());
  }
}
